import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconBrandWhatsapp, IconUsersGroup, IconX } from '@tabler/icons-react';

const WA_NUMBER = '8801700559595';
const WA_TEXT = 'আসসালামু আলাইকুম, আমি সহজ কাজ সম্পর্কে জানতে চাই।';
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(WA_TEXT)}`;

const GROUP_LINK = 'https://chat.whatsapp.com/CWnBikjhgO767CR1AhtjuM?s=cl&p=a&mlu=4';

const HIDDEN_ON = [
    '/generate-image',
    '/generate-image-manual',
    '/ai-templates',
    '/mobile-upload',
    '/template-sets',
    '/worldcup-jersey',
    '/bulk-photo-edit',
    '/print-media/editor',
    '/admin',
];

function trackWhatsapp(method) {
    try {
        if (typeof window !== 'undefined') {
            if (window.fbq) window.fbq('track', 'Contact', { method });
            if (window.gtag) window.gtag('event', 'whatsapp_click', { method });
        }
    } catch (_) {
        /* tracking must never block navigation */
    }
}

export default function WhatsappFloat() {
    const location = useLocation();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    // Full-screen modals (e.g. invoice settings) can ask us to hide so we don't
    // overlap their controls; they dispatch these window events on open/close.
    const [suppressed, setSuppressed] = useState(false);
    useEffect(() => {
        const hide = () => setSuppressed(true);
        const show = () => setSuppressed(false);
        window.addEventListener('sohozkaj:hide-whatsapp', hide);
        window.addEventListener('sohozkaj:show-whatsapp', show);
        return () => {
            window.removeEventListener('sohozkaj:hide-whatsapp', hide);
            window.removeEventListener('sohozkaj:show-whatsapp', show);
        };
    }, []);

    if (suppressed) return null;
    if (HIDDEN_ON.some(prefix => location.pathname.startsWith(prefix))) return null;

    const options = [
        {
            key: 'group',
            href: GROUP_LINK,
            method: 'whatsapp_group',
            title: t('support.whatsappFloat.supportGroup'),
            hint: t('support.whatsappFloat.supportGroupHint'),
            Icon: IconUsersGroup,
        },
        {
            key: 'direct',
            href: WA_LINK,
            method: 'whatsapp_direct',
            title: t('support.whatsappFloat.directMessage'),
            hint: t('support.whatsappFloat.directMessageHint'),
            Icon: IconBrandWhatsapp,
        },
    ];

    return (
        <>
            {/* Click-away backdrop */}
            {open && (
                <button
                    type="button"
                    aria-hidden="true"
                    tabIndex={-1}
                    onClick={() => setOpen(false)}
                    className="hidden lg:block fixed inset-0 z-[189] cursor-default"
                />
            )}

            {/* pointer-events-none on the wrapper so its large transparent bounding box
                (the closed option cards still occupy layout space) doesn't swallow clicks
                meant for page content underneath — e.g. a bottom-right Save button on
                narrower screens. Only the FAB and the open option links re-enable clicks. */}
            {/* `bottom` clears a tool page's sticky action bar when one is on
                screen (see useStickyBarLift) — the FAB used to sit on top of the
                tool's own run button. The FAB hugs the bottom-right corner (8px / 12px) so it stays out of the
                way of page content such as the Buy Credit page's sticky pay buttons. */}
            <div
                className="hidden lg:flex fixed right-2 xl:right-3 z-[190] flex-col items-end gap-2.5 xl:gap-3 pointer-events-none transition-[bottom] duration-300 ease-out"
                style={{ bottom: 'calc(var(--tool-action-bar-h, 0px) + 0.5rem)' }}
            >
                {/* Options */}
                {options.map(({ key, href, method, title, hint, Icon }, i) => (
                    <a
                        key={key}
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => {
                            trackWhatsapp(method);
                            setOpen(false);
                        }}
                        className={`flex items-center gap-3 origin-bottom-right transition-all duration-200 ${
                            open
                                ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                                : 'opacity-0 translate-y-2 scale-95 pointer-events-none'
                        }`}
                        style={{ transitionDelay: open ? `${i * 40}ms` : '0ms' }}
                    >
                        <span className="flex flex-col items-end rounded-xl bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 px-3 py-1.5 shadow-lg">
                            <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 whitespace-nowrap">
                                {title}
                            </span>
                            <span className="text-xs text-zinc-500 dark:text-zinc-400 whitespace-nowrap">
                                {hint}
                            </span>
                        </span>
                        <span className="flex items-center justify-center w-10 h-10 xl:w-12 xl:h-12 bg-[#25D366] text-white rounded-full shadow-[0_6px_18px_rgba(37,211,102,0.45)]">
                            <Icon className="w-5 h-5 xl:w-6 xl:h-6" size={24} />
                        </span>
                    </a>
                ))}

                {/* Main FAB */}
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    aria-expanded={open}
                    aria-label={open ? t('support.whatsappFloat.close') : t('support.whatsappFloat.openLabel')}
                    className="pointer-events-auto flex items-center justify-center w-12 h-12 xl:w-16 xl:h-16 bg-[#25D366] text-white rounded-full shadow-[0_8px_24px_rgba(37,211,102,0.45)] hover:brightness-105 hover:scale-105 transition-transform"
                >
                    {open ? (
                        <IconX className="w-5 h-5 xl:w-7 xl:h-7" size={28} />
                    ) : (
                        <IconBrandWhatsapp className="w-6 h-6 xl:w-8 xl:h-8" size={28} />
                    )}
                </button>
            </div>
        </>
    );
}
